import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Heart, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent, wellnessImages, siteConfig } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section with Logo */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg border-2 border-[#1E3A8A]">
              <p className="text-[#E31837] font-semibold text-lg mb-4 tracking-wide uppercase">Our Philosophy</p>
              <h1 className="text-[#1E3A8A] mb-8">
                {aboutContent.title}
              </h1>
              <p className="text-[#1E3A8A] text-xl mb-10 leading-relaxed">
                {aboutContent.description}
              </p>

              <div className="space-y-5">
                {aboutContent.philosophy.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-[#E31837]" />
                    <span className="text-[#1E3A8A] text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="border-4 border-[#1E3A8A] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={wellnessImages.meditation1}
                  alt="About Us"
                  className="w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1000px] h-[1000px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-[#1E3A8A] mb-6">
              Our <span className="text-[#E31837]">Core Values</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <Heart className="w-10 h-10" />,
                title: "Warm & Supportive",
                description: "Professional care delivered with genuine compassion. We create a safe space for transformation.",
              },
              {
                icon: <Award className="w-10 h-10" />,
                title: "Evidence-Based",
                description: "Our methods are grounded in research and clinical best practices, ensuring effective outcomes.",
              },
              {
                icon: <Users className="w-10 h-10" />,
                title: "Client-Centered",
                description: "You are the expert on your own life. We guide and empower you to discover your path.",
              },
            ].map((value, index) => (
              <Card
                key={index}
                className="card-bordered bg-white/95 backdrop-blur-sm text-center"
              >
                <CardContent className="p-10">
                  <div className="w-20 h-20 rounded-lg border-2 border-[#1E3A8A] flex items-center justify-center mx-auto mb-8 text-[#1E3A8A] bg-white">
                    {value.icon}
                  </div>
                  <h3 className="text-[#1E3A8A] font-bold text-2xl mb-4">{value.title}</h3>
                  <p className="text-[#1E3A8A] text-lg">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Voice Section */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[1100px] h-[1100px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#1E3A8A] mb-16 text-center">
              How We <span className="text-[#E31837]">Communicate</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { trait: "Empowering", desc: "You are the expert on your own life" },
                { trait: "Direct", desc: "Clear language, no jargon" },
                { trait: "Hopeful", desc: "Change is always possible" },
                { trait: "Grounded", desc: "Evidence-based approach" },
                { trait: "Integrative", desc: "All life domains connect" },
                { trait: "Professional", desc: "Warm but not casual" },
              ].map((item, index) => (
                <div
                  key={index}
                  className="card-bordered bg-white/95 backdrop-blur-sm p-6 flex items-center gap-5"
                >
                  <div className="w-4 h-4 rounded-full bg-[#E31837]" />
                  <div>
                    <h4 className="text-[#1E3A8A] font-bold text-lg">{item.trait}</h4>
                    <p className="text-[#1E3A8A]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="section-padding bg-[#F8F9FA] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <div className="border-4 border-[#1E3A8A] rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={wellnessImages.luxury}
                  alt="Clinical Integration"
                  className="w-full object-cover"
                />
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-sm p-10 rounded-lg border-2 border-[#1E3A8A]">
              <h2 className="text-[#1E3A8A] mb-8">
                Clinical <span className="text-[#E31837]">Integration</span>
              </h2>
              <p className="text-[#1E3A8A] text-xl mb-8">
                What sets us apart is our seamless integration of coaching and clinical care. 
                Our four-tier system ensures you receive the right level of support:
              </p>

              <div className="space-y-6">
                {[
                  { tier: "Tier 1-3", title: "Life Coaching", desc: "Lifestyle, supplementation, and medical support" },
                  { tier: "Tier 4", title: "Clinical Programs", desc: "Advanced optimization under medical oversight" },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="card-bordered bg-white p-6"
                  >
                    <span className="text-[#E31837] font-semibold">{item.tier}</span>
                    <h4 className="text-[#1E3A8A] font-bold text-xl mt-2">{item.title}</h4>
                    <p className="text-[#1E3A8A] mt-2">{item.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-[#1E3A8A] mt-8 text-lg">
                Our AI-powered intake system automatically routes you to the appropriate care level.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - With Silhouette */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={siteConfig.logo}
            alt=""
            className="w-[900px] h-[900px] object-contain opacity-100"
          />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl border-4 border-[#1E3A8A] p-12 shadow-[8px_8px_0px_0px_rgba(10,22,40,0.3)] text-center">
              <img
                src={siteConfig.logo}
                alt={siteConfig.name}
                className="h-20 w-20 object-contain mx-auto mb-8"
              />
              <h2 className="text-[#1E3A8A] mb-8">
                Ready to Transform Your Life?
              </h2>
              <p className="text-[#1E3A8A] text-xl mb-10">
                Let's work together to unlock your full potential.
              </p>
              <a href={siteConfig.jotformIntakeLink} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#1E3A8A] text-white border-4 border-[#1E3A8A] hover:bg-[#E31837] hover:border-[#E31837] text-lg px-10 py-4 rounded font-semibold transition-all">
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
